import React from "react";
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import styled from "@emotion/styled";
import { selectEditDefaultPaymentSchedulePerm } from "../../../../../slices/userActionsPermissionsSlice";
import { selectTeamDefaultPaymentSchedule, selectTeamPaymentSchedules } from "../../../../../slices/teamAndUserSettingsSlice";
import { FlexWrapper, ComponentSectionRow, ColumnContent, ComponentHeader, DetailWrapper } from "../../../../../StyledComponents";

const MortgageSchedules = () => {
  const theme = useTheme()

  const paymentSchedules = useSelector(selectTeamPaymentSchedules)
  const defaultPaymentSchedule = useSelector(selectTeamDefaultPaymentSchedule)
  const editDefaultPaymentSchedulePerm = useSelector(selectEditDefaultPaymentSchedulePerm)

  return (
    <FlexWrapper className="column fitContentHeight" style={{paddingBottom: '4rem'}}>
      <ComponentHeader>
        Mortgage Schedules
      </ComponentHeader>
      <ComponentSectionRow style={{padding: '1rem 0 0.5rem 0'}}>
        <ColumnContent className="detail small lowercase thin column">
          <span>Lancaster Sweep uses schedules to provide up-to-date breakdowns of the</span>
          <span>interest and principal that has been paid on any mortgage, as well as how much of each remains due.</span>
          <span>Schedules can be assigned to each mortgage individually.</span>
          <span>Contact your App Administrator to request additional schedules.</span>
        </ColumnContent>
      </ComponentSectionRow>
      <ScheduleTable>
        <ScheduleRow $header>
          <ScheduleCell $header>Name</ScheduleCell>
          <ScheduleCell $header>Role</ScheduleCell>
          <ScheduleCell $header>eMail</ScheduleCell>
        </ScheduleRow>
        {paymentSchedules.map((schedule) => (
          <ScheduleRow key={schedule._id.toString()}>
            <ScheduleCell>{schedule.label}</ScheduleCell>
            <ScheduleCell>{schedule.calculation}</ScheduleCell>
            <ScheduleCell $legend>
              <ScheduleLegend>
                {schedule.legend.map((legend) => (
                  <span key={legend.substring(0, 5)}>{legend}</span>
                ))}
              </ScheduleLegend>
            </ScheduleCell>
          </ScheduleRow>
        ))}
      </ScheduleTable>
      <ComponentSectionRow style={{padding: '1rem 0 0.5rem 0'}}>
        <ColumnContent className="detail small lowercase thin">
          <span>Default Schedule:</span>
          <span style={{fontWeight: '500', marginLeft: '0.5rem'}}>{defaultPaymentSchedule && defaultPaymentSchedule.label}</span>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.tooltip.error.color}`,
              backgroundColor: `${theme.tooltip.error.background}`,
              border: `0.3rem solid ${theme.tooltip.error.border}`,
              borderBottom: `0.2rem solid ${theme.tooltip.error.border}`,
              opacity: '0',
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -7],
              },
            }]}}
            title={''}
            placement="top"
            arrow
          >
            <DetailWrapper 
              className={!editDefaultPaymentSchedulePerm ? 'addButtonDisabled dark' : 'addButton'}
              style={{width: 'fit-content', fontSize: '0.8rem', textWrap: 'nowrap', margin: '0 0 0 0.5rem', zIndex: '100', padding: '0 0.5rem 0 0', height: '1.5rem'}}
            >
              <EditIcon style={{height: '1rem', width: '1rem', color: `${theme.palette.primary.main}`, margin: '0 0.3rem'}} /> 
              Edit
            </DetailWrapper>
          </Tooltip>
        </ColumnContent>
      </ComponentSectionRow>
    </FlexWrapper>
  )
}

const ScheduleTable = styled.div(({ theme }) => `
  width: calc(100% - 2rem);
  min-width: 0;
  margin: 2rem 1rem 0;
  overflow: hidden;
  border: 0.1rem solid ${theme.text.header.component};
  border-radius: 0.7rem;
  background: ${theme.palette.common.white};
`)

const ScheduleRow = styled.div(({ theme, $header }) => `
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(10rem, 0.8fr) minmax(11rem, 0.9fr) minmax(0, 1.55fr);
  min-height: ${$header ? "2.75rem" : "3.1rem"};
  border-top: ${$header ? "none" : `0.1rem solid ${theme.text.header.component}`};
  background: ${$header ? theme.palette.common.cardBackground : theme.palette.common.white};
`)

const ScheduleCell = styled.div(({ theme, $header, $legend }) => `
  min-width: 0;
  display: flex;
  align-items: ${$legend ? "flex-start" : "center"};
  justify-content: ${$header ? "center" : "flex-start"};
  padding: ${$header ? "0.62rem 0.85rem" : "0.7rem 0.85rem"};
  border-left: 0.1rem solid ${theme.palette.common.grayBorder};
  color: ${$header ? theme.palette.common.grayText : theme.palette.common.black};
  font-size: ${$header ? "0.74rem" : "0.88rem"};
  font-weight: ${$header ? "800" : "650"};
  line-height: 1.35;
  overflow-wrap: anywhere;
  text-transform: ${$header ? "uppercase" : "none"};

  &:first-of-type {
    border-left: none;
  }
`)

const ScheduleLegend = styled.div(({ theme }) => `
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 0.18rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.82rem;
  font-weight: 550;
  line-height: 1.32;
  text-transform: none;
`)

export default MortgageSchedules
